import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, ArrowRightLeft, AlertTriangle, Box, Boxes, MapPin, PackageCheck, PackagePlus,
    X, Delete, Layers, Check,
} from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/picking.css";
import "../styles/move-stock.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playComplete,
} from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import { apiFetch } from "../api/apiFetch";
import { useAccess } from "../access/AccessContext";
import { isManagerPlus } from "../access/positions";

// `crate` (2026-10-26): the Scan-tote scan was a stocked pallet crate stored
// in a lane. No tote session is opened — the screen asks for the crate's new
// location, confirms, and the BE moves the crate with everything in it.
// `transfer` (2026-10-26): the Tote to tote pill — a source put-away tote is
// in hand; items are picked up on the screen and the next tote scan commits
// them all to that tote. No session.
type Phase = "awaiting-tote" | "tote-open" | "crate" | "transfer";

// Tote to tote: the tote units are taken from, as the BE described it. The
// picks are held on the screen (contents.startQty − qty per SKU) until the
// destination tote is scanned, which commits them in one call.
interface TransferSource {
    toteId: string;
    category: string | null;
    units: number;
}
// How a SKU was picked up — recorded on its tote_transfers audit row.
type TransferMethod = "scan" | "manual" | "move_all";

// The crate the operator scanned, as the BE described it on open-tote.
interface CrateInfo {
    toteId: string;
    location: string;
    locationName: string;
    warehouse: string | null;
    units: number;
    skus: number;
}

// Which pill the operator is on. `move` is the original per-unit flow
// (scan location, scan each unit). `consolidate` empties whole locations
// onto the tote: one location scan moves everything, no unit scans, no
// tap (admin / superAdmin only — a whole shelf moves per scan). The mode is fixed for the life of a
// tote session (the BE stores it on the session so auto-resume lands back
// on the right pill) and the pills hide once a tote is in hand.
// `transfer` moves put-away stock from one tote to another (no session —
// every scan is one committed move); open to everyone with Move Stock.
type Mode = "move" | "consolidate" | "transfer";

// One SKU at the open source location. `qty` is the amount still there (ticks
// down as units are moved onto the tote); `startQty` is the amount present when
// the location was opened — the baseline for the moved/total progress shown on
// each row (mirrors stock check's scanned/expected).
interface Content {
    sku: string;
    title: string;
    barcode?: string | null;
    qty: number;
    startQty: number;
    // Units of this SKU the operator found here that the data didn't have —
    // booked in at this location and moved in one step (see confirmBookIn).
    // Counted into startQty so the row and location totals include them.
    bookedIn?: number;
}

// Consolidate: what the last location scan moved onto the tote. Shown in
// the prompt's own spot for a beat ("EMPTIED <location> / N units"), then
// the prompt comes back. No per-SKU list: the operator empties the whole
// shelf and doesn't need to know what was on it (per Alex, 2026-10-15).
interface ConsLast {
    displayName: string;
    count: number;
}

// Consolidate: why a location was refused whole. `on_wave` — a picker is
// walking to it (allocated stock is still physically on the shelf);
// `reserved` — an order needs the SKU and there is none elsewhere; `held`
// — a live row isn't 'available'; `empty` — nothing recorded here.
type BlockedReason = "on_wave" | "reserved" | "held" | "empty";
interface BlockedPrompt {
    reason: BlockedReason;
    location: string;
    displayName: string;
    sku?: string;
    title?: string;
}

const capTitle = (s: string) => (s && s.length > 40 ? `${s.slice(0, 40)}…` : s);

const MoveStock = () => {
    usePageTitle("move_stock");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("awaiting-tote");
    const [mode, setMode] = useState<Mode>("move");
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [toteId, setToteId] = useState<string>("");
    const [movedUnits, setMovedUnits] = useState(0);
    const [location, setLocation] = useState<string | null>(null);   // null = awaiting-location
    const [displayName, setDisplayName] = useState<string>("");
    const [contents, setContents] = useState<Content[]>([]);
    // Units present at the location when it was opened — the baseline the
    // per-location "moved / left" summary counts down from.
    const [locationStartQty, setLocationStartQty] = useState(0);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Manual move — numeric keypad bounded to qty-at-location.
    const [manualPrompt, setManualPrompt] = useState<null | { sku: string; title: string; max: number }>(null);
    const [manualQty, setManualQty] = useState("");
    // Move-all confirm.
    const [moveAllPrompt, setMoveAllPrompt] = useState(false);
    const [completePrompt, setCompletePrompt] = useState(false);
    const [reopenPrompt, setReopenPrompt] = useState<null | { tote: string; count: number }>(null);
    // "New tote or leave" after a successful complete. `empty` = released a
    // tote nothing was moved into.
    const [donePrompt, setDonePrompt] = useState<null | { movedUnits: number; empty: boolean }>(null);
    // "Book it in and move it" — a real product the data doesn't have at the
    // open location (scan → not_here / none_left). Same answer Stock Check
    // gives an unexpected SKU, carried through to the tote. `barcode` is the
    // value scanned, re-sent so the BE resolves the same product.
    const [bookInPrompt, setBookInPrompt] = useState<null | { sku: string; title: string; barcode: string }>(null);

    // Consolidate pill state: the last location emptied this session, shown
    // under the scan prompt so the operator sees the move landed.
    const [consLast, setConsLast] = useState<ConsLast | null>(null);
    // Location whose move is in flight — a big location (many SKUs, each
    // row-locked and re-aggregated) can take a second or two, and a blank
    // screen meanwhile reads as "the scan did nothing".
    const [consBusy, setConsBusy] = useState<string | null>(null);
    // Clears the "EMPTIED" confirmation back to the scan prompt.
    const consDoneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const CONS_DONE_MS = 1500;
    const [blockedPrompt, setBlockedPrompt] = useState<BlockedPrompt | null>(null);
    // Move crate: the scanned crate, and the destination awaiting confirm.
    const [crate, setCrate] = useState<CrateInfo | null>(null);
    const [crateDest, setCrateDest] = useState<null | { location: string; displayName: string }>(null);
    // Tote to tote: the source tote in hand. Its contents reuse `contents`
    // (qty = still on the tote, startQty − qty = picked up) and `movedUnits`
    // (units in hand). Rescanning the source with picks in hand asks before
    // cancelling — nothing has moved yet, but the operator is holding items.
    const [xferSource, setXferSource] = useState<TransferSource | null>(null);
    const [xferCancelPrompt, setXferCancelPrompt] = useState(false);
    // Consolidate has its own key (stock_control.move_stock.consolidate, 2026-09-30). Before the access
    // map settles, fall back to the rank rule — isManagerPlus understands both the legacy values and the
    // position names (the old literal "admin"/"superAdmin" check hid the pill once positions were renamed).
    const { authoritative, canWrite } = useAccess();
    const canConsolidate = authoritative
        ? canWrite("stock_control.move_stock.consolidate")
        : isManagerPlus(userObject.position ?? userObject.permissions);

    // Refs so async scan handlers never read stale state.
    const phaseRef = useRef(phase);
    const modeRef = useRef(mode);
    const sessionIdRef = useRef(sessionId);
    const locationRef = useRef(location);
    const toteIdRef = useRef(toteId);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { modeRef.current = mode; }, [mode]);
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
    useEffect(() => { locationRef.current = location; }, [location]);
    useEffect(() => { toteIdRef.current = toteId; }, [toteId]);
    const movedUnitsRef = useRef(movedUnits);
    const contentsRef = useRef(contents);
    const xferSourceRef = useRef<TransferSource | null>(null);
    const xferMethodsRef = useRef<Map<string, TransferMethod>>(new Map());
    useEffect(() => { movedUnitsRef.current = movedUnits; }, [movedUnits]);
    useEffect(() => { contentsRef.current = contents; }, [contents]);

    const mountedRef = useRef(true);

    const identity = () => ({
        uid: userObject.uid,
        name: userObject.name,
        warehouse: userObject.warehouse,
    });

    const post = async (path: string, body: Record<string, unknown>) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...body, ...identity() }),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        return res;
    };

    const applyMode = (next: Mode) => {
        setMode(next);
        modeRef.current = next;
    };

    // Auto-resume the operator's own open tote on mount (Book-In parity).
    // Backing out of the screen closes the tote (see handleBack and the
    // unmount cleanup below), so this only catches a refresh, a crash or an
    // app eviction mid-tote — we drop them back in on the pill the session
    // was opened with.
    useEffect(() => {
        mountedRef.current = true;
        (async () => {
            try {
                const q = new URLSearchParams({
                    uid: String(userObject.uid ?? ""),
                    warehouse: String(userObject.warehouse ?? ""),
                }).toString();
                const res = await apiFetch(`/move-stock/active?${q}`, { method: "GET" });
                if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
                const data = await res.json().catch(() => null);
                if (mountedRef.current && data?.active) {
                    setSessionId(data.active.sessionId);
                    setToteId(data.active.toteId);
                    const activeLines = data.active.lines;
                    setMovedUnits(Array.isArray(activeLines) && activeLines.length > 0
                        ? activeLines.reduce((a: number, l: any) => a + Number(l.moved_qty || 0), 0)
                        : Number(data.active.movedUnits ?? 0));
                    applyMode(data.active.mode === "consolidate" ? "consolidate" : "move");
                    setLocation(null);
                    setConsLast(null);
                    setPhase("tote-open");
                }
            } catch { /* no active session — start fresh */ }
        })();
        return () => {
            mountedRef.current = false;
            if (consDoneTimerRef.current) clearTimeout(consDoneTimerRef.current);
            // Safety net for every exit that doesn't go through handleBack
            // (browser / Android back, session expiry, a route change from
            // elsewhere): an open tote is closed on the way out so it never
            // sits in_progress holding its stock at IN-TRANSIT. handleBack
            // and finishTote null the ref once they've closed it, so this
            // never fires a second complete for the same session.
            const open = sessionIdRef.current;
            if (open === null) return;
            sessionIdRef.current = null;
            apiFetch(`/move-stock/complete-tote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId: open, ...identity() }),
            }).catch(() => { /* left in_progress — auto-resume picks it up */ });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Open tote -----------------------------------------------------

    const applyOpenToteSuccess = (data: any) => {
        setSessionId(data.sessionId);
        setToteId(data.toteId);
        setMovedUnits((data.lines || []).reduce((a: number, l: any) => a + Number(l.moved_qty || 0), 0));
        setLocation(null);
        setContents([]);
        setLocationStartQty(0);
        setConsLast(null);
        setPhase("tote-open");
        playSuccess();
    };

    const handleOpenTote = async (val: string) => {
        const res = await post(`/move-stock/open-tote`, { toteId: val, mode: modeRef.current });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        if (!data.valid) {
            const reason: string = data.reason || "";
            if (reason === "closed_reopenable") {
                setReopenPrompt({ tote: val, count: Number(data.movedUnits ?? 0) });
                return;
            }
            if (reason === "crate" && data.crate) {
                // A stocked pallet crate: move it whole rather than reclaim
                // it as an empty tote (that would orphan its contents).
                const c = data.crate;
                setCrate({
                    toteId: String(c.toteId ?? val),
                    location: String(c.location ?? ""),
                    locationName: String(c.locationName ?? c.location ?? ""),
                    warehouse: c.warehouse ?? null,
                    units: Number(c.units ?? 0),
                    skus: Number(c.skus ?? 0),
                });
                setCrateDest(null);
                setPhase("crate");
                playSuccess();
                return;
            }
            if (reason === "closed_goods_in") {
                playError();
                toast.error(t("move_stock_closed_goods_in"), { toastId: "ms-open-tote" });
                return;
            }
            playError();
            // A tote the operator has open in Put Away → Consolidate (same
            // engine, other screen) comes back with source 'consolidation':
            // send them there rather than saying "finish your open tote"
            // about a tote this screen can't show.
            const inConsolidate = data.source === "consolidation";
            const msg =
                reason === "locked" && inConsolidate
                                                  ? t("move_stock_tote_in_consolidate", { tote: val })
              : reason === "locked"             ? t("move_stock_tote_locked", { name: data.openedBy || "" })
              : reason === "user_has_open_tote" && inConsolidate
                                                  ? t("move_stock_tote_in_consolidate", { tote: data.openToteId || "" })
              : reason === "user_has_open_tote" ? t("move_stock_user_has_open_tote", { tote: data.openToteId || "" })
              : reason === "status_open"        ? t("move_stock_tote_open")
              : reason === "status_closed"      ? t("move_stock_tote_closed")
              : reason === "status_stored"      ? t("move_stock_tote_stored")
              : reason === "not_a_tote"         ? t("move_stock_not_a_tote")
              :                                   t("move_stock_not_a_tote");
            toast.error(msg, { toastId: "ms-open-tote" });
            return;
        }
        applyOpenToteSuccess(data);
    };

    const confirmReopenTote = async () => {
        if (!reopenPrompt) return;
        const tote = reopenPrompt.tote;
        setSubmitting(true);
        try {
            const res = await post(`/move-stock/open-tote`, { toteId: tote, reopen: true, mode: modeRef.current });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.valid) {
                playError();
                // Beaten to the tote between prompt and confirm — say who has it.
                const msg = data?.reason === "locked"
                    ? t("move_stock_tote_locked", { name: data.openedBy || "" })
                    : t("server_error");
                toast.error(msg, { toastId: "ms-server" });
                return;
            }
            applyOpenToteSuccess(data);
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
            }
        } finally {
            if (mountedRef.current) {
                setSubmitting(false);
                setReopenPrompt(null);
            }
        }
    };

    // ---- Open source location ------------------------------------------

    const locationErrorMessage = (reason: string) =>
        reason === "not_active"       ? t("move_stock_session_ended")
      : reason === "wrong_warehouse"  ? t("move_stock_wrong_warehouse")
      : reason === "not_a_source"     ? t("move_stock_not_a_source")
      :                                 t("move_stock_unknown_location");

    const handleOpenLocation = async (val: string) => {
        const res = await post(`/move-stock/open-location`, { sessionId: sessionIdRef.current, location: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        if (!data.valid) {
            playError();
            const reason: string = data.reason || "";
            if (reason === "not_active") resetToTote();
            toast.error(locationErrorMessage(reason), { toastId: "ms-open-loc" });
            return;
        }
        const mapped = (data.contents || []).map((c: any) => ({
            sku: c.sku, title: c.title, barcode: c.barcode ?? null,
            qty: Number(c.qty), startQty: Number(c.qty),
        }));
        setLocation(data.location);
        setDisplayName(data.displayName || val);
        setContents(mapped);
        setLocationStartQty(mapped.reduce((a: number, c: Content) => a + c.qty, 0));
        playSuccess();
    };

    // ---- Scan a product (move 1) ---------------------------------------

    const handleScanProduct = async (val: string) => {
        const res = await post(`/move-stock/scan`, {
            sessionId: sessionIdRef.current, location: locationRef.current, barcode: val,
        });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        switch (data.outcome) {
            case "matched": {
                setContents(prev => prev.map(c =>
                    c.sku === data.sku ? { ...c, qty: Math.max(c.qty - Number(data.movedForSku || 1), 0) } : c));
                setMovedUnits(m => m + Number(data.movedForSku || 1));
                playSuccess();
                break;
            }
            case "not_here":
            case "none_left": {
                // The operator is holding a unit the data doesn't have here
                // (never recorded, or every recorded unit is already on the
                // tote). Offer to book it in and move it rather than a
                // dead-end error — the confirm guards against a double scan.
                if (data.outcome === "none_left") {
                    setContents(prev => prev.map(c => c.sku === data.sku ? { ...c, qty: 0 } : c));
                }
                playError();
                if (typeof data.sku === "string" && data.sku) {
                    setBookInPrompt({
                        sku: data.sku,
                        title: typeof data.title === "string" && data.title ? data.title : data.sku,
                        barcode: val,
                    });
                } else {
                    toast.error(t(data.outcome === "none_left" ? "move_stock_none_left" : "move_stock_not_here"), { toastId: "ms-not-here" });
                }
                break;
            }
            case "not_active": {
                toast.error(t("move_stock_session_ended"), { toastId: "ms-ended" });
                resetToTote();
                break;
            }
            default: { // "unknown"
                playError();
                toast.error(t("move_stock_barcode_not_recognised"), { toastId: "ms-unknown" });
            }
        }
    };

    // ---- Consolidate: one location scan empties it ----------------------

    // A refusal from the move. The location is refused WHOLE (per Alex,
    // 2026-09-29): nothing moved, pop-up says why.
    const showBlocked = (data: any, scanned: string) => {
        playError();
        setBlockedPrompt({
            reason: data.reason as BlockedReason,
            location: data.location || scanned,
            displayName: data.displayName || data.location || scanned,
            sku: typeof data.sku === "string" ? data.sku : undefined,
            title: typeof data.title === "string" ? data.title : undefined,
        });
    };

    const isBlockedReason = (reason: string): reason is BlockedReason =>
        reason === "on_wave" || reason === "reserved" || reason === "held" || reason === "empty";

    // The Consolidate pill's location scan IS the move (per Alex,
    // 2026-09-29: one scan, no tap; admin / superAdmin only). The BE runs
    // the whole-location guard inside the move transaction and either
    // empties the location or refuses it whole.
    //
    // Scanner double-fire: a Zebra trigger held a beat too long sends the
    // same barcode twice ~100ms apart. The first scan empties the shelf,
    // so the second would only ever answer "empty" — swallow a repeat of
    // the location just moved (or still moving) inside a short window.
    const consMoveGuardRef = useRef<{ location: string; at: number } | null>(null);
    const CONS_REPEAT_MS = 2000;

    const handleConsolidateScan = async (val: string) => {
        const key = val.toUpperCase();
        const guard = consMoveGuardRef.current;
        if (guard && guard.location === key && Date.now() - guard.at < CONS_REPEAT_MS) return;
        consMoveGuardRef.current = { location: key, at: Date.now() };
        setSubmitting(true);
        setConsBusy(val);
        try {
            const res = await post(`/move-stock/consolidate-move`, { sessionId: sessionIdRef.current, location: val });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            if (!data.success) {
                const reason: string = data.reason || "";
                if (isBlockedReason(reason)) { showBlocked(data, val); return; }
                playError();
                if (reason === "not_active") resetToTote();
                toast.error(locationErrorMessage(reason), { toastId: "ms-open-loc" });
                return;
            }
            const moved = Number(data.removed || 0);
            setMovedUnits(m => m + moved);
            setConsLast({ displayName: data.displayName || val, count: moved });
            if (consDoneTimerRef.current) clearTimeout(consDoneTimerRef.current);
            consDoneTimerRef.current = setTimeout(() => {
                consDoneTimerRef.current = null;
                if (mountedRef.current) setConsLast(null);
            }, CONS_DONE_MS);
            playBoxAdd();
            if (Array.isArray(data.releasedTotes) && data.releasedTotes.length > 0) {
                toast.info(t("move_stock_cons_released_totes", { totes: data.releasedTotes.join(", ") }), {
                    toastId: "ms-released-totes",
                });
            }
        } finally {
            // Keep the repeat guard's timestamp at the moment the move
            // settled, so the window covers a double-fire that lands
            // while the request is in flight.
            consMoveGuardRef.current = { location: key, at: Date.now() };
            if (mountedRef.current) { setSubmitting(false); setConsBusy(null); }
        }
    };

    // ---- Move crate: destination scan + confirm ---------------------------

    // The destination is looked up first so the confirm can name it as the
    // operator knows it; the BE re-validates everything inside the move.
    const handleCrateDestination = async (val: string) => {
        if (!crate) return;
        if (val.toUpperCase() === crate.toteId.toUpperCase()) {
            // Rescanning the crate backs out, as a tote rescan does elsewhere.
            cancelCrate();
            return;
        }
        if (val === crate.location) {
            playError();
            toast.error(t("move_stock_crate_same_location"), { toastId: "ms-crate-dest" });
            return;
        }
        const res = await post(`/put-away/location/check`, { locationId: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        if (!data.valid) {
            playError();
            toast.error(t("move_stock_unknown_location"), { toastId: "ms-crate-dest" });
            return;
        }
        setCrateDest({ location: val, displayName: data.displayName || val });
        playSuccess();
    };

    const crateErrorMessage = (reason: string, title?: string) =>
        reason === "same_location"     ? t("move_stock_crate_same_location")
      : reason === "not_a_destination" ? t("move_stock_crate_not_a_destination")
      : reason === "unknown_location"  ? t("move_stock_unknown_location")
      : reason === "on_wave"           ? t("move_stock_crate_on_wave", { title: title || "" })
      : reason === "not_a_crate"       ? t("move_stock_crate_gone")
      :                                  t("server_error");

    const confirmCrateMove = async () => {
        if (!crate || !crateDest) return;
        setSubmitting(true);
        try {
            const res = await post(`/move-stock/move-crate`, { toteId: crate.toteId, locationId: crateDest.location });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            if (!data.success) {
                playError();
                toast.error(crateErrorMessage(String(data.reason || ""), data.title), { toastId: "ms-crate-move" });
                if (data.reason === "not_a_crate") cancelCrate();
                else setCrateDest(null);
                return;
            }
            playComplete();
            toast.success(
                t("move_stock_crate_moved", {
                    crate: crate.toteId, to: data.toName || crateDest.displayName, count: String(data.units ?? crate.units),
                }),
                { toastId: "ms-crate-moved" },
            );
            cancelCrate();
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    const cancelCrate = () => {
        setCrate(null);
        setCrateDest(null);
        setPhase("awaiting-tote");
    };

    // ---- Tote to tote: move put-away stock between totes ------------------
    //
    // Scan the tote to move from, scan the items (or tap a row / Move all),
    // then scan the tote to move to — that last scan commits every pick in
    // one BE transaction and both totes are back in the Put Away queue (the
    // source freed if it ran dry). No session (plan
    // feat-tote-to-tote-transfer.md): nothing is written before the
    // destination scan, so a refresh just starts again at the source scan.

    const transferRefusalMessage = (reason: string, data: any) =>
        reason === "not_a_tote"            ? t("move_stock_not_a_tote")
      : reason === "same_tote"             ? t("move_stock_xfer_same_tote")
      : reason === "in_use"                ? (data?.openedBy
                                                ? t("move_stock_tote_locked", { name: String(data.openedBy) })
                                                : t("move_stock_tote_open"))
      : reason === "not_awaiting_put_away" ? t("move_stock_xfer_not_awaiting")
      : reason === "holds_picked_stock"    ? t("move_stock_xfer_picked")
      : reason === "empty"                 ? t("move_stock_xfer_empty")
      : reason === "wrong_warehouse"       ? t("move_stock_xfer_wrong_warehouse")
      : reason === "category_mismatch"     ? t("move_stock_xfer_category", {
                                                category: data?.category ? String(data.category) : t("move_stock_xfer_no_category"),
                                            })
      : reason === "crate"                 ? t("move_stock_xfer_crate")
      : reason === "not_a_destination"     ? t("move_stock_xfer_not_destination")
      :                                      t("server_error");

    const setTransferSource = (src: TransferSource | null) => { setXferSource(src); xferSourceRef.current = src; };

    const handleTransferSource = async (val: string) => {
        const res = await post(`/move-stock/transfer/source`, { fromToteId: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        if (!data.valid) {
            playError();
            toast.error(transferRefusalMessage(String(data.reason || ""), data), { toastId: "ms-xfer-source" });
            return;
        }
        const mapped: Content[] = (data.contents || []).map((c: any) => ({
            sku: c.sku, title: c.title, barcode: c.barcode ?? null,
            qty: Number(c.qty), startQty: Number(c.qty),
        }));
        setTransferSource({ toteId: String(data.toteId || val), category: data.category ?? null, units: Number(data.units ?? 0) });
        setContents(mapped);
        contentsRef.current = mapped;
        setLocationStartQty(mapped.reduce((a, c) => a + c.qty, 0));
        setMovedUnits(0);
        movedUnitsRef.current = 0;
        xferMethodsRef.current = new Map();
        setPhase("transfer");
        playSuccess();
    };

    // Pick units of a SKU up off the source — on the screen only, committed
    // by the destination scan. Keypad / Move all override 'scan' on the
    // audit row for that SKU.
    const pickUp = (sku: string, qty: number, method: TransferMethod) => {
        if (qty <= 0) return;
        const next = contentsRef.current.map(c => c.sku === sku ? { ...c, qty: Math.max(c.qty - qty, 0) } : c);
        contentsRef.current = next;
        setContents(next);
        movedUnitsRef.current += qty;
        setMovedUnits(movedUnitsRef.current);
        const prev = xferMethodsRef.current.get(sku);
        if (!prev || prev === "scan") xferMethodsRef.current.set(sku, method);
    };

    const transferLines = () => contentsRef.current
        .filter(c => c.startQty - c.qty > 0)
        .map(c => ({ sku: c.sku, qty: c.startQty - c.qty, method: xferMethodsRef.current.get(c.sku) ?? "scan" }));

    // The destination scan: commit every pick in one call, then start again
    // at the source prompt. A refused destination keeps the picks so another
    // tote can be scanned; a refused source (put away meanwhile) starts over.
    const commitTransfer = async (toToteId: string) => {
        const src = xferSourceRef.current;
        if (!src) return;
        const lines = transferLines();
        if (lines.length === 0) {
            playError();
            toast.error(t("move_stock_xfer_nothing_in_hand"), { toastId: "ms-xfer-none" });
            return;
        }
        const res = await post(`/move-stock/transfer/commit`, { fromToteId: src.toteId, toToteId, lines });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        if (!data.success) {
            playError();
            const reason = String(data.reason || "");
            toast.error(
                reason === "none_left" ? t("move_stock_none_left") : transferRefusalMessage(reason, data),
                { toastId: "ms-xfer-refused" },
            );
            if (data.side === "source" || reason === "none_left") resetToTote();
            return;
        }
        playComplete();
        toast.success(
            t("move_stock_xfer_moved_toast", { count: String(data.moved ?? 0), from: src.toteId, to: toToteId }),
            { toastId: "ms-xfer-moved" },
        );
        if (data.sourceEmptied === true) {
            toast.info(t("move_stock_xfer_emptied_toast", { from: src.toteId }), { toastId: "ms-xfer-emptied" });
        }
        resetToTote();
    };

    // Anything scanned after the source: an item (pick one up) or the tote
    // to move to (commit). The BE tells them apart.
    const handleTransferScan = async (val: string) => {
        const src = xferSourceRef.current;
        if (!src) return;
        const res = await post(`/move-stock/transfer/item`, { fromToteId: src.toteId, barcode: val });
        if (!res) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            playError();
            toast.error(t("server_error"), { toastId: "ms-server" });
            return;
        }
        switch (data.outcome) {
            case "matched": {
                const row = contentsRef.current.find(c => c.sku === data.sku);
                if (!row || row.qty <= 0) {
                    playError();
                    toast.error(t("move_stock_none_left"), { toastId: "ms-not-here" });
                    return;
                }
                pickUp(String(data.sku), 1, "scan");
                playSuccess();
                break;
            }
            case "tote": await commitTransfer(String(data.toteId || val)); break;
            case "not_here":
                playError();
                toast.error(t("move_stock_xfer_not_here"), { toastId: "ms-not-here" });
                break;
            case "refused":
                playError();
                toast.error(transferRefusalMessage(String(data.reason || ""), data), { toastId: "ms-xfer-refused" });
                resetToTote();
                break;
            default:
                playError();
                toast.error(t("move_stock_barcode_not_recognised"), { toastId: "ms-unknown" });
        }
    };

    // Rescanning the source (or Cancel) with picks in hand: nothing has
    // moved, so confirm before dropping the list — the operator is holding
    // the items and puts them back.
    const requestTransferCancel = () => {
        if (movedUnitsRef.current === 0) { resetToTote(); return; }
        setXferCancelPrompt(true);
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) return;
        setLoading(true);
        try {
            if (phaseRef.current === "awaiting-tote") {
                if (modeRef.current === "transfer") await handleTransferSource(val);
                else await handleOpenTote(val);
            }
            else if (phaseRef.current === "crate") await handleCrateDestination(val);
            else if (phaseRef.current === "transfer") {
                const src = xferSourceRef.current;
                if (!src) resetToTote();
                // Rescanning the source backs out (confirmed if picks are in hand).
                else if (val.toUpperCase() === src.toteId.toUpperCase()) requestTransferCancel();
                else await handleTransferScan(val);
            }
            else if (val.toUpperCase() === toteIdRef.current.toUpperCase()) {
                // Rescanning the tote asks how to close it (Complete, or
                // Complete & Put Away) — the option lives on the confirm.
                setCompletePrompt(true);
                return;
            }
            else if (modeRef.current === "consolidate") await handleConsolidateScan(val);
            else if (locationRef.current === null) await handleOpenLocation(val);
            else await handleScanProduct(val);
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
            }
        } finally {
            if (mountedRef.current) { setLoading(false); setScanValue(""); }
        }
    };

    // ---- Book in and move (unit the data doesn't have here) -------------

    const confirmBookIn = async () => {
        if (!bookInPrompt) return;
        const prompt = bookInPrompt;
        setSubmitting(true);
        try {
            const res = await post(`/move-stock/book-in-move`, {
                sessionId: sessionIdRef.current, location: locationRef.current, barcode: prompt.barcode,
            });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            if (data.outcome === "not_active") {
                toast.error(t("move_stock_session_ended"), { toastId: "ms-ended" });
                resetToTote();
                return;
            }
            if (data.outcome !== "matched") {
                playError();
                toast.error(
                    data.outcome === "none_left" ? t("move_stock_none_left")
                  : data.outcome === "unknown"   ? t("move_stock_barcode_not_recognised")
                  :                                t("server_error"),
                    { toastId: "ms-book-in" },
                );
                return;
            }
            const moved = Number(data.movedForSku || 1);
            const bookedIn = data.bookedIn !== false;
            setContents(prev => {
                const found = prev.find(c => c.sku === data.sku);
                if (found) {
                    return prev.map(c => c.sku !== data.sku ? c : {
                        ...c,
                        // A booked-in unit was never in the opening snapshot:
                        // grow the baseline so the row reads moved/total
                        // correctly. A plain move (SKU was here after all)
                        // just ticks qty down like a scan.
                        qty: bookedIn ? c.qty : Math.max(c.qty - moved, 0),
                        startQty: bookedIn ? c.startQty + moved : c.startQty,
                        bookedIn: (c.bookedIn ?? 0) + (bookedIn ? moved : 0),
                    });
                }
                return [...prev, {
                    sku: data.sku,
                    title: typeof data.title === "string" && data.title ? data.title : prompt.title,
                    barcode: data.barcode ?? null,
                    qty: 0, startQty: moved, bookedIn: bookedIn ? moved : 0,
                }];
            });
            if (bookedIn) setLocationStartQty(q => q + moved);
            setMovedUnits(m => m + moved);
            playBoxAdd();
        } finally {
            if (mountedRef.current) { setSubmitting(false); setBookInPrompt(null); }
        }
    };

    // ---- Manual move (numeric keypad) ----------------------------------

    const openManual = (c: Content) => {
        if (c.qty <= 0) return;
        setManualPrompt({ sku: c.sku, title: c.title, max: c.qty });
        setManualQty("");
    };

    const keypadPress = (key: string) => {
        if (!manualPrompt) return;
        if (key === "del") { setManualQty(q => q.slice(0, -1)); return; }
        const next = (manualQty + key).replace(/^0+/, "");
        const n = Number(next);
        if (!Number.isFinite(n)) return;
        // Clamp to the available qty so the operator can't move more than is there.
        setManualQty(n > manualPrompt.max ? String(manualPrompt.max) : next);
    };

    const moveAllOfSku = () => {
        if (!manualPrompt) return;
        setManualQty(String(manualPrompt.max));
    };

    const confirmManual = async () => {
        if (!manualPrompt) return;
        const qty = Number(manualQty);
        if (!qty || qty <= 0) return;
        setSubmitting(true);
        try {
            if (phaseRef.current === "transfer") {
                // Tote to tote: picks stay on the screen until the destination scan.
                pickUp(manualPrompt.sku, qty, "manual");
                playBoxAdd();
                return;
            }
            const res = await post(`/move-stock/move-manual`, {
                sessionId: sessionIdRef.current, location: locationRef.current, sku: manualPrompt.sku, qty,
            });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                playError();
                toast.error(data?.reason === "none_left" ? t("move_stock_none_left") : t("server_error"), { toastId: "ms-server" });
                return;
            }
            const moved = Number(data.removed || 0);
            setContents(prev => prev.map(c =>
                c.sku === manualPrompt.sku ? { ...c, qty: Number(data.locationRemaining ?? Math.max(c.qty - moved, 0)) } : c));
            setMovedUnits(m => m + moved);
            playBoxAdd();
        } finally {
            if (mountedRef.current) { setSubmitting(false); setManualPrompt(null); setManualQty(""); }
        }
    };

    // ---- Move all of the location --------------------------------------

    const confirmMoveAll = async () => {
        setSubmitting(true);
        try {
            if (phaseRef.current === "transfer") {
                // Tote to tote: pick up everything left; committed on the destination scan.
                for (const c of contentsRef.current) pickUp(c.sku, c.qty, "move_all");
                playBoxAdd();
                return;
            }
            const res = await post(`/move-stock/move-all`, {
                sessionId: sessionIdRef.current, location: locationRef.current,
            });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            const moved = Number(data.removed || 0);
            setContents(prev => prev.map(c => ({ ...c, qty: 0 })));
            setMovedUnits(m => m + moved);
            playBoxAdd();
        } finally {
            if (mountedRef.current) { setSubmitting(false); setMoveAllPrompt(false); }
        }
    };

    // ---- Complete tote -------------------------------------------------

    // Close the tote. `putAwayNow` (Complete & Put Away) carries the tote
    // straight into Put Away with the same silent claim Book In's
    // "Complete & Putaway" uses — the operator is already holding it. An
    // empty tote is just released either way.
    const finishTote = async (putAwayNow = false) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const res = await post(`/move-stock/complete-tote`, { sessionId: sessionIdRef.current });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            playComplete();
            const empty = data.empty === true;
            // The session is closed from here on, whichever exit follows.
            // Null the ref synchronously (not just via the state effect) so
            // Leave / back / unmount see no open tote and don't close it again.
            clearSession();
            if (putAwayNow && !empty) {
                const tote = toteIdRef.current;
                setCompletePrompt(false);
                resetToTote();
                navigate("/put-away", { state: { autoClaimTote: tote } });
                return;
            }
            setDonePrompt({ movedUnits, empty });
        } finally {
            if (mountedRef.current) { setSubmitting(false); setCompletePrompt(false); }
        }
    };

    const clearSession = () => {
        setSessionId(null);
        sessionIdRef.current = null;
    };

    const resetToTote = () => {
        clearSession();
        setToteId("");
        setMovedUnits(0);
        setLocation(null);
        setDisplayName("");
        setContents([]);
        setLocationStartQty(0);
        setConsLast(null);
        movedUnitsRef.current = 0;
        setTransferSource(null);
        setXferCancelPrompt(false);
        xferMethodsRef.current = new Map();
        setPhase("awaiting-tote");
    };

    const openNewTote = () => {
        setDonePrompt(null);
        resetToTote();
    };

    // Backing out of the module closes the tote (per Alex, 2026-10-13): the
    // operator shouldn't have to find the Complete button first, and a tote
    // left in_progress held its stock at IN-TRANSIT outside any queue until
    // they came back. Same BE call as Complete — moved units are handed to
    // Put Away, an empty tote is released. If the close fails we stay put
    // with the error so the tote isn't silently left open.
    const handleBack = async () => {
        const open = sessionIdRef.current;
        if (open === null) {
            navigate("/stock-control");
            return;
        }
        if (submitting) return;
        setSubmitting(true);
        try {
            const tote = toteIdRef.current;
            const res = await post(`/move-stock/complete-tote`, { sessionId: open });
            if (!res) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
                return;
            }
            clearSession();
            playComplete();
            toast.success(
                data.empty === true
                    ? t("move_stock_auto_released", { tote })
                    : t("move_stock_auto_sent", { tote, count: String(data.movedUnits ?? movedUnits) }),
                { toastId: "ms-auto-close" },
            );
            navigate("/stock-control");
        } catch {
            if (mountedRef.current) {
                playError();
                toast.error(t("server_error"), { toastId: "ms-server" });
            }
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    const toteCommand = t(mode === "transfer" ? "move_stock_xfer_scan_source" : "move_stock_scan_tote")
        .toUpperCase().split(/[\s-]+/).filter(Boolean);
    const xferItemsCommand = t("move_stock_xfer_scan_items").toUpperCase().split(/[\s-]+/).filter(Boolean);
    const locationCommand = t(mode === "consolidate" ? "move_stock_cons_scan_location" : "move_stock_scan_location")
        .toUpperCase().split(/[\s-]+/).filter(Boolean);
    const busyCommand = t("move_stock_cons_moving").toUpperCase().split(/[\s-]+/).filter(Boolean);
    const doneCommand = t("move_stock_cons_emptied").toUpperCase().split(/[\s-]+/).filter(Boolean);
    const crateCommand = t("move_stock_crate_scan_location").toUpperCase().split(/[\s-]+/).filter(Boolean);
    const modalOpen = manualPrompt !== null || moveAllPrompt || completePrompt || reopenPrompt !== null
        || donePrompt !== null || bookInPrompt !== null || blockedPrompt !== null || crateDest !== null
        || xferCancelPrompt;
    const hasStock = contents.some(c => c.qty > 0);
    // Per-location progress: units still at the open location vs how many have
    // been moved off it (mirrors stock check's matched/expected counter).
    const leftAtLocation = contents.reduce((a, c) => a + c.qty, 0);
    const movedFromLocation = Math.max(locationStartQty - leftAtLocation, 0);

    const blockedBody = (p: BlockedPrompt) => {
        const vars = { location: p.displayName, title: p.title || p.sku || "" };
        return p.reason === "on_wave"  ? t("move_stock_cons_blocked_on_wave", vars)
             : p.reason === "reserved" ? t("move_stock_cons_blocked_reserved", vars)
             : p.reason === "held"     ? t("move_stock_cons_blocked_held", vars)
             :                           t("move_stock_cons_blocked_empty", vars);
    };

    // One tappable row per SKU: moved/total, green when fully moved, orange
    // when part moved. Shared by the Move list and the Tote to tote list;
    // `interactive` false renders the rows read-only (Tote to tote before
    // the destination is scanned).
    const contentRows = (interactive: boolean) => contents.map(c => {
        const total = c.startQty;
        const moved = Math.max(total - c.qty, 0);
        const done = total > 0 && c.qty <= 0;            // green — fully moved
        const partial = moved > 0 && c.qty > 0;          // orange — part moved
        return (
            <button
                key={c.sku}
                className={`move-stock-row${done ? " move-stock-row--done" : ""}${partial ? " move-stock-row--partial" : ""}`}
                onClick={() => openManual(c)}
                disabled={!interactive || c.qty <= 0 || submitting}
            >
                <div className="move-stock-row-tick">
                    {done
                        ? <Check size={18} />
                        : <span className={`move-stock-row-dot${partial ? " move-stock-row-dot--partial" : ""}`} />}
                </div>
                <div className="move-stock-row-main">
                    <span className="move-stock-row-title">{capTitle(c.title)}</span>
                    <span className="move-stock-row-identity">
                        <span className="move-stock-row-sku">{c.sku}</span>
                        {c.barcode && <span className="move-stock-row-barcode">{c.barcode}</span>}
                        {(c.bookedIn ?? 0) > 0 && (
                            <span className="move-stock-row-booked">· {t("move_stock_booked_in")}</span>
                        )}
                    </span>
                </div>
                <div className="move-stock-row-count">
                    {moved}<span className="move-stock-row-count-sep">/</span>{total}
                </div>
            </button>
        );
    });

    return (
        <div
            className="scan-panel move-stock-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <div className="page-title-bar">
                <h1 className="page-title">{t("move_stock")}</h1>
                {/* Move / Consolidate / Tote to tote pills — same icon toggle
                    as Put Away's mode switch. Hidden once a tote is in hand:
                    the mode is fixed for the pair / session. Consolidate is
                    its own access key (the BE enforces it too), so basic
                    users see Move and Tote to tote only. */}
                {phase === "awaiting-tote" && (
                    <div className="picking-tier-toggle" role="radiogroup" aria-label={t("move_stock_mode_toggle")}>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "move"}
                            className={`picking-tier-pill${mode === "move" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => applyMode("move")}
                            title={t("move_stock_mode_move")}
                            aria-label={t("move_stock_mode_move")}
                        >
                            <ArrowRightLeft size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                        {canConsolidate && (
                            <button
                                type="button"
                                role="radio"
                                aria-checked={mode === "consolidate"}
                                className={`picking-tier-pill${mode === "consolidate" ? " picking-tier-pill--active" : ""}`}
                                onClick={() => applyMode("consolidate")}
                                title={t("move_stock_mode_consolidate")}
                                aria-label={t("move_stock_mode_consolidate")}
                            >
                                <Layers size={16} strokeWidth={2.2} aria-hidden="true" />
                            </button>
                        )}
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "transfer"}
                            className={`picking-tier-pill${mode === "transfer" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => applyMode("transfer")}
                            title={t("move_stock_mode_transfer")}
                            aria-label={t("move_stock_mode_transfer")}
                        >
                            <Boxes size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                    </div>
                )}
                <button className="page-back-btn" onClick={handleBack} disabled={submitting} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {phase === "awaiting-tote" && (
                <div className="scan-centre">
                    <div className="scan-command-wrap">
                        <div className="scan-command">
                            {toteCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                        </div>
                    </div>
                </div>
            )}

            {phase === "crate" && crate && (
                <div className="move-stock-body" data-testid="move-stock-crate">
                    <div className="move-stock-header">
                        <div className="move-stock-tote">
                            <Box size={18} />
                            <strong>{t("move_stock_crate_title")}</strong>
                        </div>
                    </div>
                    <div className="move-stock-crate-card">
                        <Box size={28} />
                        <div>
                            <div className="move-stock-crate-id">{crate.toteId}</div>
                            <div className="move-stock-crate-meta">
                                {t("move_stock_crate_at", { location: crate.locationName })} · {t("move_stock_crate_units", { count: String(crate.units) })}
                            </div>
                        </div>
                    </div>
                    <div className="scan-centre move-stock-cons-centre">
                        <div className="scan-command-wrap">
                            <div className="scan-command">
                                {crateCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                            </div>
                        </div>
                    </div>
                    <div className="move-stock-actions">
                        <button className="btn btn-ghost btn-lg" onClick={cancelCrate} disabled={submitting}>
                            {t("move_stock_crate_cancel")}
                        </button>
                    </div>
                </div>
            )}

            {/* Tote to tote: the source tote and what has been picked up off
                it. Scan items (or tap a row / Move all) to pick them up, then
                scan the tote to move to — that commits the lot and the screen
                returns to the source prompt. */}
            {phase === "transfer" && xferSource && (
                <div className="move-stock-body" data-testid="move-stock-transfer">
                    <div className="move-stock-header">
                        <div className="move-stock-tote" data-testid="move-stock-xfer-source">
                            <Box size={18} />
                            <strong>{xferSource.toteId}</strong>
                        </div>
                        <div className="move-stock-moved">
                            <span className="move-stock-moved-num">{movedUnits}</span>
                            <span className="move-stock-moved-label">{t("move_stock_xfer_in_hand")}</span>
                        </div>
                    </div>

                    {/* In normal flow (not the absolutely positioned scan-centre):
                        the SKU list sits beneath it, so an overlay prompt would
                        print across the rows. */}
                    <div className="scan-centre move-stock-xfer-centre">
                        <div className="scan-command-wrap">
                            <div className="scan-command" data-testid="move-stock-xfer-prompt">
                                {xferItemsCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                                <div className="scan-command-sub">{t("move_stock_xfer_then_tote")}</div>
                            </div>
                        </div>
                    </div>

                    <div className="move-stock-locsummary">
                        <div className="move-stock-locsummary-cell">
                            <span className="move-stock-locsummary-num">{movedFromLocation}</span>
                            <span className="move-stock-locsummary-label">{t("move_stock_xfer_in_hand")}</span>
                        </div>
                        <div className="move-stock-locsummary-cell">
                            <span className="move-stock-locsummary-num">{leftAtLocation}</span>
                            <span className="move-stock-locsummary-label">{t("move_stock_xfer_on_tote")}</span>
                        </div>
                    </div>

                    <div className="move-stock-list">
                        {contents.every(c => c.qty <= 0) && (
                            <div className="move-stock-empty">{t("move_stock_xfer_all_in_hand")}</div>
                        )}
                        {contentRows(true)}
                    </div>

                    <div className="move-stock-actions">
                        {hasStock && (
                            <button
                                className="btn btn-ghost btn-lg"
                                onClick={() => setMoveAllPrompt(true)}
                                disabled={submitting}
                            >
                                <Layers size={20} />
                                {t("move_stock_move_all")}
                            </button>
                        )}
                        <button className="btn btn-ghost btn-lg" onClick={requestTransferCancel} disabled={submitting}>
                            {t("move_stock_crate_cancel")}
                        </button>
                    </div>
                </div>
            )}

            {phase === "tote-open" && mode === "consolidate" && (
                <div className="move-stock-body" data-testid="move-stock-consolidate">
                    <div className="move-stock-header">
                        <div className="move-stock-tote">
                            <Box size={18} />
                            <strong>{toteId}</strong>
                        </div>
                        <div className="move-stock-moved">
                            <span className="move-stock-moved-num">{movedUnits}</span>
                            <span className="move-stock-moved-label">{t("move_stock_moved")}</span>
                        </div>
                    </div>

                    {/* One spot, three states: MOVING <location> while the
                        request is in flight; a green EMPTIED <location> +
                        unit count for a beat once it lands; then the scan
                        prompt again. Nothing is rendered beneath the prompt
                        — the old per-SKU list sat in normal flow under an
                        absolutely positioned prompt and the two collided on
                        the handheld. */}
                    <div className="scan-centre move-stock-cons-centre">
                        <div className="scan-command-wrap">
                            {consBusy ? (
                                <div className="scan-command move-stock-cons-busy" data-testid="move-stock-cons-busy">
                                    {busyCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                                    <FitWord>{consBusy.toUpperCase()}</FitWord>
                                </div>
                            ) : consLast ? (
                                <div className="scan-command move-stock-cons-done" data-testid="move-stock-cons-last" role="status">
                                    <span className="move-stock-cons-done-icon" aria-hidden="true"><Check size={44} strokeWidth={3} /></span>
                                    {doneCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                                    <FitWord>{consLast.displayName.toUpperCase()}</FitWord>
                                    <div className="scan-command-sub move-stock-cons-done-sub">
                                        {consLast.count} {t("move_stock_cons_units")}
                                    </div>
                                </div>
                            ) : (
                                <div className="scan-command">
                                    {locationCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="move-stock-actions">
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={() => setCompletePrompt(true)}
                            disabled={submitting}
                        >
                            <PackageCheck size={20} />
                            {t("move_stock_complete")}
                        </button>
                    </div>
                </div>
            )}

            {phase === "tote-open" && mode === "move" && (
                <div className="move-stock-body">
                    <div className="move-stock-header">
                        <div className="move-stock-tote">
                            <Box size={18} />
                            <strong>{toteId}</strong>
                        </div>
                        <div className="move-stock-moved">
                            <span className="move-stock-moved-num">{movedUnits}</span>
                            <span className="move-stock-moved-label">{t("move_stock_moved")}</span>
                        </div>
                    </div>

                    {location === null ? (
                        <div className="scan-centre">
                            <div className="scan-command-wrap">
                                <div className="scan-command">
                                    {locationCommand.map((word, i) => <FitWord key={i}>{word}</FitWord>)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="move-stock-locbar">
                                <div className="move-stock-locbar-loc">
                                    <MapPin size={16} />
                                    <strong>{displayName}</strong>
                                </div>
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => { setLocation(null); setContents([]); setLocationStartQty(0); }}
                                    disabled={submitting}
                                >
                                    {t("move_stock_switch_location")}
                                </button>
                            </div>

                            <div className="move-stock-locsummary">
                                <div className="move-stock-locsummary-cell">
                                    <span className="move-stock-locsummary-num">{movedFromLocation}</span>
                                    <span className="move-stock-locsummary-label">{t("move_stock_moved")}</span>
                                </div>
                                <div className="move-stock-locsummary-cell">
                                    <span className="move-stock-locsummary-num">{leftAtLocation}</span>
                                    <span className="move-stock-locsummary-label">{t("move_stock_left")}</span>
                                </div>
                            </div>

                            <div className="move-stock-list">
                                {contents.length === 0 && (
                                    <div className="move-stock-empty">{t("move_stock_empty_location")}</div>
                                )}
                                {contents.length > 0 && contents.every(c => c.qty <= 0) && (
                                    <div className="move-stock-empty">{t("move_stock_location_empty_hint")}</div>
                                )}
                                {contentRows(true)}
                            </div>
                        </>
                    )}

                    <div className="move-stock-actions">
                        {location !== null && hasStock && (
                            <button
                                className="btn btn-ghost btn-lg"
                                onClick={() => setMoveAllPrompt(true)}
                                disabled={submitting}
                            >
                                <Layers size={20} />
                                {t("move_stock_move_all")}
                            </button>
                        )}
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={() => setCompletePrompt(true)}
                            disabled={submitting}
                        >
                            <PackageCheck size={20} />
                            {t("move_stock_complete")}
                        </button>
                    </div>
                </div>
            )}

            {/* Block the scanner only while a modal is open or during the
                tote-open transition (sessionId not yet set). Never disable
                mid-move — HiddenScanInput drops keystrokes while disabled. */}
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={modalOpen || (loading && phase === "awaiting-tote")}
            />

            {/* Manual move — numeric keypad */}
            {manualPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setManualPrompt(null)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_manual_title")}</h2>
                            <button className="modal-close" onClick={() => setManualPrompt(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="move-stock-prod-card">
                                <Box size={28} />
                                <div>
                                    <div className="move-stock-prod-title">{manualPrompt.title}</div>
                                    <div className="move-stock-prod-sku">{manualPrompt.sku}</div>
                                </div>
                            </div>
                            <div className="move-stock-keypad-display">{manualQty || "0"}</div>
                            <div className="move-stock-keypad-max">
                                {t("move_stock_manual_max", { max: String(manualPrompt.max) })}
                            </div>
                            <div className="move-stock-keypad">
                                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map(k => (
                                    <button key={k} className="move-stock-key" onClick={() => keypadPress(k)} disabled={submitting}>{k}</button>
                                ))}
                                <button className="move-stock-key" onClick={moveAllOfSku} disabled={submitting} title={t("move_stock_move_all")}>
                                    {t("move_stock_all_short")}
                                </button>
                                <button className="move-stock-key" onClick={() => keypadPress("0")} disabled={submitting}>0</button>
                                <button className="move-stock-key" onClick={() => keypadPress("del")} disabled={submitting}>
                                    <Delete size={20} />
                                </button>
                            </div>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setManualPrompt(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={confirmManual}
                                    disabled={submitting || !manualQty || Number(manualQty) <= 0}
                                >
                                    {submitting ? t("submitting") : t("move_stock_manual_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Book in and move — unit the data doesn't have here */}
            {bookInPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setBookInPrompt(null)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_book_in_title")}</h2>
                            <button className="modal-close" onClick={() => setBookInPrompt(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="move-stock-prod-card">
                                <PackagePlus size={28} />
                                <div>
                                    <div className="move-stock-prod-title">{bookInPrompt.title}</div>
                                    <div className="move-stock-prod-sku">{bookInPrompt.sku}</div>
                                </div>
                            </div>
                            <p className="move-stock-modal-hint">
                                {t("move_stock_book_in_body", { location: displayName })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setBookInPrompt(null)} disabled={submitting}>
                                    {t("no")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={confirmBookIn} disabled={submitting}>
                                    {submitting ? t("submitting") : t("move_stock_book_in_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Consolidate — location refused whole (allocated / reserved /
                held / empty). Nothing moved; one exit. */}
            {blockedPrompt && (
                <div className="modal-overlay" onClick={() => setBlockedPrompt(null)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()} role="alertdialog">
                        <div className="modal-card-header">
                            <h2>{t("move_stock_cons_blocked_title")}</h2>
                            <button className="modal-close" onClick={() => setBlockedPrompt(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="move-stock-prod-card move-stock-prod-card--blocked">
                                <AlertTriangle size={28} />
                                <div>
                                    <div className="move-stock-prod-title">{blockedPrompt.title || blockedPrompt.displayName}</div>
                                    <div className="move-stock-prod-sku">{blockedPrompt.sku || blockedPrompt.location}</div>
                                </div>
                            </div>
                            <p className="move-stock-modal-hint">{blockedBody(blockedPrompt)}</p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-primary btn-lg" onClick={() => setBlockedPrompt(null)}>
                                    {t("move_stock_cons_blocked_ok")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Move crate confirm — destination scanned, one Yes moves the
                crate and everything in it. */}
            {crate && crateDest && (
                <div className="modal-overlay" onClick={() => !submitting && setCrateDest(null)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_crate_confirm_title")}</h2>
                            <button className="modal-close" onClick={() => setCrateDest(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="move-stock-prod-card">
                                <Box size={28} />
                                <div>
                                    <div className="move-stock-prod-title">{crate.toteId}</div>
                                    <div className="move-stock-prod-sku">{t("move_stock_crate_units", { count: String(crate.units) })}</div>
                                </div>
                            </div>
                            <div className="move-stock-crate-route" data-testid="move-stock-crate-route">
                                <span>{crate.locationName}</span>
                                <ArrowRightLeft size={18} className="move-stock-crate-arrow" aria-hidden="true" />
                                <span>{crateDest.displayName}</span>
                            </div>
                            <p className="move-stock-modal-hint">
                                {t("move_stock_crate_confirm_body", {
                                    crate: crate.toteId, count: String(crate.units),
                                    from: crate.locationName, to: crateDest.displayName,
                                })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setCrateDest(null)} disabled={submitting}>
                                    {t("no")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={confirmCrateMove} disabled={submitting}>
                                    {submitting ? t("submitting") : t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Move-all confirm */}
            {moveAllPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setMoveAllPrompt(false)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_move_all_title")}</h2>
                            <button className="modal-close" onClick={() => setMoveAllPrompt(false)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="move-stock-modal-hint">
                                {phase === "transfer" && xferSource
                                    ? t("move_stock_xfer_move_all_confirm", { from: xferSource.toteId })
                                    : t("move_stock_move_all_confirm")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setMoveAllPrompt(false)} disabled={submitting}>
                                    {t("no")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={confirmMoveAll} disabled={submitting}>
                                    {submitting ? t("submitting") : t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Complete confirm — Complete (queue it) or Complete & Put Away
                (straight into Put Away with the tote in hand). An empty
                tote only offers release. */}
            {completePrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setCompletePrompt(false)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_complete_title")}</h2>
                            <button className="modal-close" onClick={() => setCompletePrompt(false)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="move-stock-modal-hint">
                                {movedUnits > 0
                                    ? t("move_stock_complete_confirm", { count: String(movedUnits) })
                                    : t("move_stock_complete_empty_confirm")}
                            </p>
                            {movedUnits > 0 ? (
                                <div className="modal-confirm-buttons move-stock-complete-buttons">
                                    <button className="btn btn-ghost btn-lg" onClick={() => setCompletePrompt(false)} disabled={submitting}>
                                        {t("no")}
                                    </button>
                                    <button className="btn btn-secondary btn-lg" onClick={() => finishTote(false)} disabled={submitting}>
                                        {submitting ? t("submitting") : t("move_stock_complete_only")}
                                    </button>
                                    <button className="btn btn-primary btn-lg" onClick={() => finishTote(true)} disabled={submitting}>
                                        {submitting ? t("submitting") : t("move_stock_complete_put_away")}
                                    </button>
                                </div>
                            ) : (
                                <div className="modal-confirm-buttons">
                                    <button className="btn btn-ghost btn-lg" onClick={() => setCompletePrompt(false)} disabled={submitting}>
                                        {t("no")}
                                    </button>
                                    <button className="btn btn-success btn-lg" onClick={() => finishTote(false)} disabled={submitting}>
                                        {submitting ? t("submitting") : t("yes")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Re-open closed Move Stock tote */}
            {reopenPrompt && (
                <div className="modal-overlay" onClick={() => !submitting && setReopenPrompt(null)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_stock_reopen_title")}</h2>
                            <button className="modal-close" onClick={() => setReopenPrompt(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="move-stock-modal-hint">
                                {t("move_stock_reopen_body", {
                                    tote: reopenPrompt.tote,
                                    count: String(reopenPrompt.count),
                                })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setReopenPrompt(null)} disabled={submitting}>
                                    {t("no")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={confirmReopenTote} disabled={submitting}>
                                    {submitting ? t("submitting") : t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tote to tote cancel with picks in hand: nothing has moved, the
                operator puts the items back on the source. */}
            {xferCancelPrompt && xferSource && (
                <div className="modal-overlay" onClick={() => setXferCancelPrompt(false)}>
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()} data-testid="move-stock-xfer-cancel">
                        <div className="modal-card-header">
                            <h2>{t("move_stock_xfer_cancel_title")}</h2>
                            <button className="modal-close" onClick={() => setXferCancelPrompt(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="move-stock-modal-hint">
                                {t("move_stock_xfer_cancel_body", { count: String(movedUnits), from: xferSource.toteId })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => setXferCancelPrompt(false)}>
                                    {t("no")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={resetToTote}>
                                    {t("yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New tote or leave */}
            {donePrompt && (
                <div className="modal-overlay">
                    <div className="modal-card move-stock-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{donePrompt.empty ? t("move_stock_released_title") : t("move_stock_sent_title")}</h2>
                        </div>
                        <div className="modal-card-body">
                            <p className="move-stock-modal-hint">
                                {donePrompt.empty
                                    ? t("move_stock_released_body")
                                    : t("move_stock_sent_body", { count: String(donePrompt.movedUnits) })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost btn-lg" onClick={() => { setDonePrompt(null); handleBack(); }}>
                                    {t("move_stock_leave")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={openNewTote}>
                                    {t("move_stock_open_new")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MoveStock;
